import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from '../../components/ui/Button';
import { Tag } from 'primereact/tag';
import { SelectButton } from 'primereact/selectbutton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../Layout/Layout';
import CampaignWizard from './wizard/CampaignWizard';
import AutomationsPanel from './AutomationsPanel';
import InsightsPanel from './InsightsPanel';
import { useGetCampaigns, useGetMarketingQuota, useCancelCampaign } from '../../queries/marketing';
import SubTabs from '../../nav/SubTabs';
import { useInHub } from '../../nav/useInHub';

const STATUS_SEVERITY = {
  draft: 'secondary',
  scheduled: 'info',
  sending: 'warning',
  completed: 'success',
  canceled: 'danger',
  failed: 'danger',
};

const CANCELABLE = ['draft', 'scheduled', 'sending'];

const VALID_TABS = ['campaigns', 'automations', 'insights'];

export default function CampaignsIndex() {
  const { t } = useTranslation();
  const inHub = useInHub();
  const [searchParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'campaigns';
  const initialSegment = searchParams.get('segment') || null;   // "message this segment" deep-link
  const [wizardOpen, setWizardOpen] = useState(!!initialSegment);
  const [view, setView] = useState(initialTab);

  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const params = useMemo(() => ({ page: page + 1, per_page: perPage }), [page, perPage]);

  const { data: list, isLoading } = useGetCampaigns(params);
  const { data: quota } = useGetMarketingQuota();
  const cancel = useCancelCampaign();

  const rows = list?.data ?? [];
  const totalRecords = list?.pagination?.total ?? 0;

  const emailQuota = useMemo(
    () => (quota?.channels ?? []).find((c) => c.channel === 'email'),
    [quota]
  );

  const confirmCancel = (row) => {
    confirmDialog({
      message: t('marketing.cancel_confirm', { name: row.name }),
      header: t('marketing.cancel_header'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => cancel.mutate(row.id),
    });
  };

  if (wizardOpen) {
    return (
      <Layout>
        <div className="p-3 md:p-4">
          <CampaignWizard onExit={() => setWizardOpen(false)} initialSegment={initialSegment} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ConfirmDialog />
      <div className="p-3 md:p-4">
        {/* Header */}
        <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mb-3">
          {!inHub && (
            <div>
              <h2 className="m-0">{t('marketing.title')}</h2>
              <p className="m-0 mt-1 text-500">{t('marketing.subtitle')}</p>
            </div>
          )}
          {view === 'campaigns' && (
            <Button icon="pi pi-plus" label={t('marketing.new_campaign')} onClick={() => setWizardOpen(true)} />
          )}
        </div>

        {/* Tabs: Campaigns | Automations | Insights | Analytics */}
        <div className="mb-3">
          {inHub ? (
            <SubTabs ariaLabel={t('marketing.title')} items={[
              { id: 'campaigns', label: t('marketing.tab_campaigns'), active: view === 'campaigns', onClick: () => setView('campaigns') },
              { id: 'automations', label: t('marketing.tab_automations'), active: view === 'automations', onClick: () => setView('automations') },
              { id: 'insights', label: t('marketing.tab_insights'), active: view === 'insights', onClick: () => setView('insights') },
            ]} />
          ) : (
          <SelectButton value={view} onChange={(e) => e.value && setView(e.value)}
            options={[
              { label: t('marketing.tab_campaigns'), value: 'campaigns' },
              { label: t('marketing.tab_automations'), value: 'automations' },
              { label: t('marketing.tab_insights'), value: 'insights' },
            ]} />
          )}
        </div>

        {view === 'automations' ? (
          <AutomationsPanel />
        ) : view === 'insights' ? (
          <InsightsPanel onViewChange={setView} />
        ) : (
          <>
        {/* Quota card */}
        {emailQuota && (
          <div className="surface-card border-round p-3 mb-3 flex align-items-center gap-3">
            <span className="flex align-items-center justify-content-center border-circle"
              style={{ width: 42, height: 42, background: 'var(--primary-100)' }}>
              <i className="pi pi-envelope" style={{ color: 'var(--primary-600)' }} />
            </span>
            <div>
              <div className="text-500 text-sm">{t('marketing.email_quota')}</div>
              <div className="text-xl font-bold">
                {emailQuota.unlimited
                  ? t('marketing.unlimited')
                  : t('marketing.remaining_of', {
                      remaining: emailQuota.remaining,
                      limit: (emailQuota.monthly_limit ?? 0) + (emailQuota.extra_purchased ?? 0),
                    })}
              </div>
            </div>
          </div>
        )}

        {/* List */}
        <DataTable value={rows} loading={isLoading} lazy paginator
          first={page * perPage} rows={perPage} totalRecords={totalRecords}
          onPage={(e) => { setPage(e.page); setPerPage(e.rows); }}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage={t('marketing.empty')} dataKey="id" responsiveLayout="scroll">
          <Column field="name" header={t('marketing.col_name')}
            body={(r) => r.name || <span className="text-400">—</span>} />
          <Column field="status" header={t('marketing.col_status')}
            body={(r) => <Tag severity={STATUS_SEVERITY[r.status] ?? null} value={t(`marketing.status_${r.status}`)} />} />
          <Column header={t('marketing.col_progress')}
            body={(r) => `${r.sent_count ?? 0} / ${r.recipients_total ?? 0}`} />
          <Column field="scheduled_at" header={t('marketing.col_when')}
            body={(r) => (r.scheduled_at ? new Date(r.scheduled_at).toLocaleString() : '—')} />
          <Column header="" style={{ width: '6rem' }}
            body={(r) => CANCELABLE.includes(r.status) ? (
              <Button icon="pi pi-times" rounded text severity="danger"
                tooltip={t('marketing.cancel')} onClick={() => confirmCancel(r)} />
            ) : null} />
        </DataTable>
          </>
        )}
      </div>
    </Layout>
  );
}
